import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Box, 
  Chip,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Person,
  LocationOn,
  Home,
  DownhillSkiing,
  Receipt,
  CalendarMonth,
  Phone,
  Email,
  CreditCard,
  DirectionsBus,
  Groups,
  Star
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/layout/Header';
import { TravellerAccessService } from '../services/travellerAccessService';
import { TravellerOverviewData } from '../types/travellerAccess';
import { format } from 'date-fns';
import { de, enGB } from 'date-fns/locale';
import { getSkiPassPriceAtTripStart } from '../utils/calculations';

const TravellerOverviewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<TravellerOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError(t('traveller.overview.invalidToken'));
        setLoading(false);
        return;
      }

      try {
        const travellerData = await TravellerAccessService.getTravellerData(token);
        setData(travellerData);
      } catch (err) {
        console.error('Error loading traveller data:', err);
        // Check if it's a specific error type for better user experience
        const errorMessage = (err as Error).message;
        if (errorMessage.includes('Invalid access token') || errorMessage.includes('inactive')) {
          setError(t('traveller.overview.linkExpired'));
        } else {
          setError(t('traveller.overview.loadError'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, t]);

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'de' ? de : enGB;
    return format(new Date(dateString), 'dd.MM.yyyy', { locale });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  const calculateSkiPassPrice = () => {
    if (!data?.skiPass || !data?.traveller.dateOfBirth || !data?.trip.startDate || !data?.trip.skiPassAgeLevels) {
      return data?.skiPass?.price || 0;
    }

    // Use the utility function from calculations
    return getSkiPassPriceAtTripStart(
      data.traveller.dateOfBirth,
      data.trip.startDate,
      data.trip.skiPassAgeLevels,
      data.skiPass
    );
  };

  if (loading) {
    return (
      <Box sx={{ height: '100%' }}>
        <Header 
          title={t('traveller.overview.title')} 
          onBack={() => navigate('/')}
        />
        <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t('traveller.overview.loading')}
          </Typography>
        </Container>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ height: '100%' }}>
        <Header 
          title={t('traveller.overview.title')} 
          onBack={() => navigate('/')}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">
            {error || t('traveller.overview.noData')}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Header 
        title={t('traveller.overview.title')} 
        onBack={() => navigate('/')}
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" color="text.secondary">
            {data.trip.name} - {data.trip.destination}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Chip 
              icon={<CalendarMonth />} 
              label={`${formatDate(data.trip.startDate)} - ${formatDate(data.trip.endDate)}`}
              variant="outlined"
            />
            <Chip 
              label={t(`traveller.status.${data.traveller.status}`)}
              color={getStatusColor(data.traveller.status) as 'success' | 'warning' | 'error' | 'default'}
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person />
                  {t('traveller.overview.personalInfo')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemIcon><Person /></ListItemIcon>
                    <ListItemText 
                      primary={t('traveller.overview.name')}
                      secondary={data.traveller.name}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Email /></ListItemIcon>
                    <ListItemText 
                      primary={t('traveller.overview.email')}
                      secondary={data.traveller.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Phone /></ListItemIcon>
                    <ListItemText 
                      primary={t('traveller.overview.phone')}
                      secondary={data.traveller.phone}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CalendarMonth /></ListItemIcon>
                    <ListItemText 
                      primary={t('traveller.overview.dateOfBirth')}
                      secondary={formatDate(data.traveller.dateOfBirth)}
                    />
                  </ListItem>
                  {data.traveller.isSkiClubMember && (
                    <ListItem>
                      <ListItemIcon><CreditCard /></ListItemIcon>
                      <ListItemText 
                        primary={t('traveller.overview.membershipFee')}
                        secondary={data.traveller.membershipFee ? `€${data.traveller.membershipFee}` : t('traveller.overview.included')}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Apartment Information */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Home />
                  {t('traveller.overview.accommodation')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {data.apartment ? (
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Home /></ListItemIcon>
                      <ListItemText 
                        primary={t('traveller.overview.apartmentName')}
                        secondary={data.apartment.name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><LocationOn /></ListItemIcon>
                      <ListItemText 
                        primary={t('traveller.overview.apartmentClass')}
                        secondary={t(`apartment.class.${data.apartment.class}`)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Groups /></ListItemIcon>
                      <ListItemText 
                        primary={t('traveller.overview.occupancy')}
                        secondary={`${data.apartment.currentOccupancy}/${data.apartment.maxOccupancy} ${t('traveller.overview.persons')}`}
                      />
                    </ListItem>
                    {data.apartment.amenities && data.apartment.amenities.length > 0 && (
                      <ListItem>
                        <ListItemIcon><Star /></ListItemIcon>
                        <ListItemText 
                          primary={t('traveller.overview.amenities')}
                          secondary={data.apartment.amenities.join(', ')}
                        />
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <Alert severity="info">
                    {t('traveller.overview.noApartmentAssigned')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Ski Pass Information */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DownhillSkiing />
                  {t('traveller.overview.skiPass')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {data.skiPass ? (
                  <List dense>
                    <ListItem>
                      <ListItemIcon><DownhillSkiing /></ListItemIcon>
                      <ListItemText 
                        primary={t('traveller.overview.skiPassType')}
                        secondary={data.skiPass.name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CreditCard /></ListItemIcon>
                      <ListItemText 
                        primary={t('traveller.overview.price')}
                        secondary={`${calculateSkiPassPrice()} €`}
                      />
                    </ListItem>
                    {data.skiPass.description && (
                      <ListItem>
                        <ListItemText 
                          primary={t('traveller.overview.description')}
                          secondary={data.skiPass.description}
                        />
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <Alert severity="info">
                    {t('traveller.overview.noSkiPassSelected')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Transportation Information */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsBus />
                  {t('traveller.overview.transportation')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {data.traveller.transportationType ? (
                  <List dense>
                    <ListItem>
                      <ListItemIcon><DirectionsBus /></ListItemIcon>
                      <ListItemText 
                        primary={t('traveller.overview.transportationType')}
                        secondary={data.traveller.transportationType}
                      />
                    </ListItem>
                  </List>
                ) : (
                  <Alert severity="info">
                    {t('traveller.overview.noTransportationSelected')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Information */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt />
                  {t('traveller.overview.billing')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {data.billing ? (
                  <Box>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h6" color="primary">
                            €{data.billing.totalAmount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('traveller.overview.totalAmount')}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h6" color="success.main">
                            €{data.billing.paidAmount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('traveller.overview.paidAmount')}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h6" color={data.billing.outstandingAmount > 0 ? 'error' : 'success.main'}>
                            €{data.billing.outstandingAmount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('traveller.overview.outstandingAmount')}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    {data.billing.bills && data.billing.bills.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {t('traveller.overview.billDetails')}
                        </Typography>
                        <List>
                          {data.billing.bills.map((bill) => (
                            <ListItem key={bill.id}>
                              <ListItemIcon><Receipt /></ListItemIcon>
                              <ListItemText 
                                primary={bill.description}
                                secondary={bill.dueDate ? `${t('traveller.overview.dueDate')}: ${formatDate(bill.dueDate)}` : ''}
                              />
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2">€{bill.amount}</Typography>
                                <Chip 
                                  size="small"
                                  label={t(`bill.status.${bill.status}`)}
                                  color={bill.status === 'paid' ? 'success' : 'warning'}
                                />
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Alert severity="info">
                    {t('traveller.overview.noBillingInfo')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Notes */}
          {data.traveller.notes && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('traveller.overview.notes')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1">
                    {data.traveller.notes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default TravellerOverviewPage;
